import {ChangeDetectionStrategy, Component, computed, signal} from "@angular/core"
import {
    DEFAULT_NUM_SHADOW_MAP_INNER_SMOOTH_ITERATIONS,
    DEFAULT_NUM_SHADOW_MAP_INNER_UPDATE_ITERATIONS,
    DEFAULT_NUM_SHADOW_MAP_OUTER_SMOOTH_ITERATIONS,
    DEFAULT_NUM_SHADOW_MAP_OUTER_UPDATE_ITERATIONS,
    DEFAULT_SHADOW_MAP_RESOLUTION,
} from "@app/template-editor/services/three-scene-manager.service"
import {EnvironmentMapMode, SceneProperties, ScenePropertiesParameters, TextureResolution, TextureStreamingMode, UiStyle} from "@cm/template-nodes"
import {
    resolveSelectionPossibilityValue,
    SelectionPossibilities,
    SelectionPossibility,
    ValueSlotComponent,
} from "@app/template-editor/components/value-slot/value-slot.component"
import {BaseInspectorComponent} from "@app/template-editor/components/inspectors/base-inspector/base-inspector.component"
import {InspectorSectionComponent} from "@app/template-editor/components/inspectors/inspector-section/inspector-section.component"

type ShadowCatcherFalloffProperties = ScenePropertiesParameters["shadowCatcherFalloff"]

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-scene-properties-inspector",
    templateUrl: "./scene-properties-inspector.component.html",
    styleUrl: "./scene-properties-inspector.component.scss",
    imports: [ValueSlotComponent, InspectorSectionComponent],
})
export class ScenePropertiesInspectorComponent extends BaseInspectorComponent<SceneProperties, {}> {
    readonly $shadowCatcherFalloff = computed(() => {
        const {shadowCatcherFalloff} = this.$parameters()
        return shadowCatcherFalloff
    })
    readonly $realtimeShadowMapOptions = computed(() => {
        const {realtimeShadowMapOptions} = this.$parameters()
        return realtimeShadowMapOptions
    })
    readonly $supportsSectionedMenu = computed(() => {
        const {uiStyle} = this.$parameters()
        return uiStyle === "icons" || uiStyle === "accordion"
    })
    readonly $uiStylePossibilites = signal<SelectionPossibilities<UiStyle>>([
        {value: "default", name: "Dropdown"},
        {value: "icons", name: "Icons"},
        {value: "hidden", name: "Hidden"},
        {value: "accordion", name: "Accordion"},
        {value: "mobileTabs", name: "Mobile Tabs"},
    ])
    readonly $iconSizePossibilites = signal<SelectionPossibilities<number>>([
        {value: 24, name: "24px"},
        {value: 32, name: "32px"},
        {value: 40, name: "40px"},
    ])
    readonly $textureResolutionPossibilites = signal<SelectionPossibilities<TextureResolution | undefined>>([
        {value: undefined, name: "Default"},
        {value: "500px", name: "500 px"},
        {value: "1000px", name: "1000 px"},
        {value: "2000px", name: "2000 px"},
        {value: "original", name: "Original"},
    ])
    readonly $textureFilteringPossibilites = signal<SelectionPossibilities<boolean>>([
        {value: true, name: "On"},
        {value: false, name: "Off"},
    ])
    readonly $textureStreamingModePossibilites = signal<SelectionPossibilities<TextureStreamingMode | undefined>>([
        {value: undefined, name: "Default"},
        {value: "off", name: "Off"},
        {value: "low-quality", name: "Low Quality"},
        {value: "high-quality", name: "High Quality"},
    ])
    readonly $environmentMapModePossibilites = signal<SelectionPossibilities<EnvironmentMapMode>>([
        {value: "full", name: "Diffuse+Specular"},
        {value: "specularOnly", name: "Specular Only"},
    ])
    readonly $shadowCatcherFalloffPossibilites = signal<SelectionPossibilities<ShadowCatcherFalloffProperties>>([
        {value: {sizeX: 100, sizeZ: 100, smoothness: 1, opacity: 1}, name: "Yes"},
        {value: undefined, name: "No"},
    ])
    shadowCatcherFalloffSelected = (selectionPossibility: SelectionPossibility<ShadowCatcherFalloffProperties>) => {
        const shadowCatcherFalloff = this.$shadowCatcherFalloff()
        const selectionPossibilityValue = resolveSelectionPossibilityValue(selectionPossibility.value)
        if (!shadowCatcherFalloff) return selectionPossibilityValue === undefined
        return selectionPossibilityValue !== undefined
    }
    readonly $realtimeShadowMapOptionsPossibilites = signal<SelectionPossibilities<ScenePropertiesParameters["realtimeShadowMapOptions"]>>([
        {
            value: {
                resolution: DEFAULT_SHADOW_MAP_RESOLUTION,
                outerUpdateIterations: DEFAULT_NUM_SHADOW_MAP_OUTER_UPDATE_ITERATIONS,
                innerUpdateIterations: DEFAULT_NUM_SHADOW_MAP_INNER_UPDATE_ITERATIONS,
                outerSmoothIterations: DEFAULT_NUM_SHADOW_MAP_OUTER_SMOOTH_ITERATIONS,
                innerSmoothIterations: DEFAULT_NUM_SHADOW_MAP_INNER_SMOOTH_ITERATIONS,
            },
            name: "Yes",
        },
        {value: undefined, name: "No"},
    ])
    realtimeShadowMapOptionsSelected = (selectionPossibility: SelectionPossibility<ScenePropertiesParameters["realtimeShadowMapOptions"]>) => {
        const realtimeShadowMapOptions = this.$realtimeShadowMapOptions()
        const selectionPossibilityValue = resolveSelectionPossibilityValue(selectionPossibility.value)
        if (!realtimeShadowMapOptions) return selectionPossibilityValue === undefined
        return selectionPossibilityValue !== undefined
    }
}
